import "../Category/service-category.scss";
import "./service.scss";

import PageTitleBreadcrumb from "@frontend/js/Components/Shared/PageTitleBreadcrumb/PageTitleBreadcrumb";
import AnimatedVisualSwap from "@frontend/js/Components/Shared/Animation/AnimatedVisualSwap";
import SliderReviews from "@frontend/js/Components/Site/SliderReviews/SliderReviews";
import MetaHead from "@frontend/js/Components/Shared/MetaHead/MetaHead";
import ArrowNextIcon from "@frontend/images/icons/ArrowNextIcon";
import Form from "@frontend/js/Components/Shared/Form/Form";
import UsersIcon from "@frontend/images/icons/UsersIcon";
import SiteLayout from "@frontend/js/Layouts/SiteLayout";
import TimeIcon from "@frontend/images/icons/TimeIcon";
import BoxIcon from "@frontend/images/icons/BoxIcon";
import useT from "@frontend/js/hooks/useT";
import useRoute from "@frontend/js/hooks/useRoute";
import { usePage } from "@inertiajs/react";
import { useState } from "react";

export default function Service({
    page,
    resource,
    reviews = [],
    meta = {
        title: "",
        description: "",
    },
}) {
    const service = resource?.data;
    const serviceCategory = service?.category;
    const pageProps = usePage().props;
    const locale = pageProps.currentLocale;
    const route = useRoute();
    const content = service?.content || "";
    const pageTitle = page?.title?.[locale] || "";
    const pageHref = route("frontend.page", { page: page?.slug });
    const [isRequestFormOpen, setIsRequestFormOpen] = useState(false);

    async function handleSubmit(
        formData,
        { reset, clearErrors, setError, source, sourceId },
    ) {
        try {
            const csrfToken = document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content");

            const response = await fetch("/contact-form", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-TOKEN": csrfToken || "",
                },
                body: JSON.stringify({
                    ...formData,
                    locale,
                    source: source || 2,
                    ...(sourceId && { sourceId }),
                }),
            });

            const text = await response.text();

            if (response.status === 422) {
                const result = JSON.parse(text);
                if (result.errors) {
                    Object.entries(result.errors).forEach(
                        ([field, messages]) => {
                            setError(field, messages[0]);
                        },
                    );
                }
                return { validationErrors: true };
            }

            if (!response.ok) {
                console.error("Form submission error:", text);
                return false;
            }

            const result = JSON.parse(text);

            if (!result.success) {
                return false;
            }

            reset();
            return true;
        } catch (error) {
            console.error("Form submission error:", error);
            return false;
        }
    }

    const feature1 = service?.feature_1;
    const feature2 = service?.feature_2;
    const feature3 = service?.feature_3;
    const youtubeLink = service?.youtube_link;
    const serviceResults = service?.results || [];
    const { t } = useT("site");

    const embedUrl = youtubeLink
        ? `https://www.youtube.com/embed/${youtubeLink}`
        : null;

    return (
        <>
            <MetaHead meta={meta} />

            <SiteLayout>
                <div className="serviceDetails page-shell">
                    <PageTitleBreadcrumb
                        title={service?.title}
                        parentTitle={serviceCategory?.title}
                        parentHref={serviceCategory?.href}
                        grandparentTitle={pageTitle}
                        grandparentHref={pageHref}
                        skipParent={!serviceCategory}
                    />
                    <div className="serviceDetails__container">
                        <div className="serviceDetails__container__text">
                            <div
                                dangerouslySetInnerHTML={{ __html: content }}
                            />
                            {serviceResults.length > 0 && (
                                <>
                                    <h2>{t("service-page-results-title")}</h2>
                                    <div className="serviceDetails__container__text__cards">
                                        {serviceResults.map((result) => (
                                            <div
                                                key={result.id}
                                                className="serviceDetails__container__text__cards__card"
                                            >
                                                <h3>{result.title}</h3>
                                                <p>{result.description}</p>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            {embedUrl && (
                                <iframe
                                    src={embedUrl}
                                    title="Case study video"
                                    loading="lazy"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    allowFullScreen
                                />
                            )}
                            {reviews?.data && reviews.data.length > 0 && (
                                <SliderReviews reviews={reviews.data} />
                            )}
                        </div>
                        <div className="serviceDetails__container__info">
                            <AnimatedVisualSwap
                                animationId={service?.animation_id}
                            />
                            <div className="serviceDetails__container__info__list">
                                {feature1 && (
                                    <p>
                                        <BoxIcon /> {feature1}
                                    </p>
                                )}
                                {feature2 && (
                                    <p>
                                        <UsersIcon /> {feature2}
                                    </p>
                                )}
                                {feature3 && (
                                    <p>
                                        <TimeIcon /> {feature3}
                                    </p>
                                )}
                            </div>
                            <button onClick={() => setIsRequestFormOpen(true)}>
                                {t("service-page-request-offer-button")}{" "}
                                <ArrowNextIcon />
                            </button>
                        </div>
                    </div>
                    <Form
                        variant="drawer"
                        isOpen={isRequestFormOpen}
                        onClose={() => setIsRequestFormOpen(false)}
                        source={2}
                        sourceId={service?.id}
                        onSubmit={handleSubmit}
                    />
                </div>
            </SiteLayout>
        </>
    );
}
